import Skeleton from "@components/scheduling/analytics/Skeleton";
import { formatCount, formatPercent, successesOf } from "@components/scheduling/analytics/format";
import { COLORS } from "@components/scheduling/analytics/chartTheme";

const MIN_WIDTH = 2;

/** Bar width as a percentage of dialed: never invisible when the count is above zero. */
function widthOf(count, calls) {
  if (!calls || !count) return 0;
  return Math.max(MIN_WIDTH, Math.min(100, (count / calls) * 100));
}

function Row({ id, label, count, calls, colour, inner }) {
  const width = widthOf(count, calls);
  const innerWidth = inner && count ? Math.min(100, ((inner.count || 0) / count) * 100) : 0;
  return (
    <div>
      <div className="mb-1 flex items-baseline justify-between text-sm">
        <span className="font-medium text-gray-700">{label}</span>
        <span className="text-gray-600">
          <span className="font-medium text-gray-900">{formatCount(count)}</span>
          <span className="ml-2 text-xs">{calls ? formatPercent(count / calls) : "–"}</span>
        </span>
      </div>
      <div className="h-6 w-full rounded bg-gray-100">
        <div
          data-testid={`funnel-bar-${id}`}
          className="relative h-6 rounded"
          style={{ width: `${width}%`, backgroundColor: colour }}
        >
          {inner && inner.count > 0 && (
            <div
              className="absolute inset-y-0 right-0 rounded-r border-l-2 border-white"
              style={{ width: `${innerWidth}%`, backgroundColor: COLORS.neutral }}
              title={`${formatCount(inner.count)} ${inner.label}`}
            />
          )}
        </div>
      </div>
      {inner && inner.count > 0 && (
        <p className="mt-1 text-xs text-gray-500">
          <span className="mr-1 inline-block h-2 w-2 rounded-sm align-middle"
                style={{ backgroundColor: COLORS.neutral }} aria-hidden="true" />
          {formatCount(inner.count)} {inner.label}
        </p>
      )}
    </div>
  );
}

/**
 * Dialed -> answered -> booked or transferred, each a bar scaled to the
 * number dialed, so the shape of the funnel is the first thing a reader
 * sees. A transfer counts as a success like a booking. Cut-off calls
 * are a labelled slice at the end of answered: the patient picked up but the
 * call never reached an outcome.
 */
export default function Funnel({ totals, loading = false }) {
  if (loading) {
    return (
      <div className="rounded-lg border border-gray-200 bg-white p-4 space-y-4" aria-busy="true">
        <Skeleton className="h-4 w-20" />
        <Skeleton className="h-6 w-full" />
        <Skeleton className="h-6 w-1/3" />
        <Skeleton className="h-6 w-1/12" />
      </div>
    );
  }
  const t = totals || {};
  const calls = t.calls || 0;
  const successes = successesOf(t);

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4">
      <h3 className="text-sm font-medium text-gray-900">Funnel</h3>
      {calls === 0 ? (
        <div className="mt-4 flex h-40 items-center justify-center rounded-lg border border-dashed border-gray-300">
          <p className="text-sm text-gray-500">No calls yet</p>
        </div>
      ) : (
        <div
          role="img"
          aria-label={`Funnel: ${formatCount(calls)} dialed, ${formatCount(t.answered)} answered, ${formatCount(successes)} booked or transferred`}
          className="mt-4 space-y-4"
        >
          <Row id="dialed" label="Dialed" count={calls} calls={calls} colour={COLORS.calls} />
          <Row id="answered" label="Answered" count={t.answered || 0} calls={calls} colour={COLORS.answered}
               inner={{ count: t.incomplete || 0, label: "cut off" }} />
          <Row id="booked" label="Booked or transferred" count={successes} calls={calls} colour={COLORS.booked} />
        </div>
      )}
    </div>
  );
}
